/**
 * Cellera Design System - Color Tokens
 * Centralized color definitions for consistent theming across the application
 */

// Base color palette - raw color values
export const colorTokens = {
  // Primary brand colors
  navy: {
    50: "#f0f4ff",
    100: "#e0eaff",
    200: "#c7d7fe",
    300: "#a5b9fc",
    400: "#8294f8",
    500: "#6366f1",
    600: "#4f46e5",
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
    950: "#1e1b4b",
    primary: "#000080", // Your brand primary
    dark: "#1a1a7e",
    darker: "#151566",
    primaryLight: "#EBF4F2",
    /** Primary (#000080) at ~10% opacity — overlays, soft tints */
    primaryAlpha10: "#0000801A",
  },

  // Blue accents
  blue: {
    50: "#E3F2FB",
    100: "#B8DDF6",
    200: "#8AC8F1",
    300: "#5CB3EC",
    400: "#2E9DE7",
    500: "#1D67B6", // Your accent blue
    600: "#1A5DA3",
    700: "#175390",
    800: "#14497D",
    900: "#113F6A",
  },

  // Neutral colors
  gray: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    500: "#6b7280",
    600: "#4b5563",
    700: "#374151",
    800: "#1f2937",
    900: "#111827",
    950: "#030712",
    text: "#5E6B7C", // Your text gray
    dark: "#010D1D", // Your dark text
    border: "#E8E8E9",
    grayLight: "#F6F6F6",
    grayDivider: "#D9D9D9",
    appointment: "#F1F1F1",
  },

  // Surface colors
  surface: {
    white: "#FFFFFF",
    background: "#F8F8F8", // Your background gray
    secondary: "#f6fcff", // Your secondary surface
    paper: "#FFFFFF",
    light: "#FCFCFC",
    blueLight: "#E2E8F4",
  },

  // Semantic colors
  semantic: {
    success: {
      50: "#f0fdf4",
      100: "#dcfce7",
      200: "#bbf7d0",
      500: "#22c55e",
      600: "#16a34a",
      700: "#15803d",
      800: "#02677f",
    },
    warning: {
      50: "#fffbeb",
      100: "#fef3c7",
      200: "#fde68a",
      500: "#f59e0b",
      600: "#d97706",
      700: "#b45309",
    },
    error: {
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      500: "#ef4444",
      600: "#dc2626",
      700: "#BA1A1A",
    },
    info: {
      50: "#f0f9ff",
      100: "#e0f2fe",
      200: "#bae6fd",
      500: "#0ea5e9",
      600: "#0284c7",
      700: "#0369a1",
    },
  },

  // line chart colors
  charts: {
    blue: "#4A90E2",
    orange: "#F5A623",
    green: "#7ED321",
    purple: "#9013FE",
  },
} as const;

// Theme-aware color mappings
export const themeColors = {
  primary: {
    main: colorTokens.navy.primary,
    dark: colorTokens.navy.dark,
    darker: colorTokens.navy.darker,
    light: colorTokens.navy[50],
    contrast: colorTokens.surface.white,
  },
  secondary: {
    main: colorTokens.blue[500],
    dark: colorTokens.blue[600],
    light: colorTokens.blue[50],
    contrast: colorTokens.surface.white,
  },
  text: {
    primary: colorTokens.gray.dark,
    secondary: colorTokens.gray.text,
    bold: colorTokens.gray[700],
    disabled: colorTokens.gray[900],
    inverse: colorTokens.surface.white,
    blueHighlight: colorTokens.blue[400],
    border: colorTokens.gray.border,
    navyDark: colorTokens.navy.dark,
    error: colorTokens.semantic.error[500],
    errorDark: colorTokens.semantic.error[700],
  },
  background: {
    default: colorTokens.surface.background,
    paper: colorTokens.surface.paper,
    secondary: colorTokens.surface.secondary,
    highlight: colorTokens.blue[100],
    dark: colorTokens.gray.dark,
    darkGreenHighlight: colorTokens.semantic.success[800],
    light: colorTokens.surface.light,
    blueLight: colorTokens.surface.blueLight,
    navy: colorTokens.navy.primary,
    grayLight: colorTokens.gray.grayLight,
    appointment: colorTokens.gray.appointment,
    grayDivider: colorTokens.gray.grayDivider,
    primaryLight: colorTokens.navy.primaryLight,
    /** Ask AI / soft message surfaces (#EBF4F2) */
    messageBubble: colorTokens.navy.primaryLight,
    /** Navy tint for borders, hovers, chips (#000080 @ ~10%) */
    primaryAlpha10: colorTokens.navy.primaryAlpha10,
  },
  border: {
    light: colorTokens.gray[200],
    main: colorTokens.gray[300],
    dark: colorTokens.gray[400],
    darker: colorTokens.gray[700],
  },
  success: colorTokens.semantic.success,
  warning: colorTokens.semantic.warning,
  error: colorTokens.semantic.error,
  info: colorTokens.semantic.info,
  charts: colorTokens.charts,
} as const;

// Export individual color functions for easy access
export const getColor = (path: string) => {
  const keys = path.split(".");
  let result = themeColors;

  for (const key of keys) {
    if (
      typeof result === "object" &&
      result !== null &&
      Object.prototype.hasOwnProperty.call(result, key)
    ) {
      // @ts-expect-error: Indexing with string for dynamic path traversal
      result = result[key];
    } else {
      console.warn(`Color path "${path}" not found`);
      return "#000000"; // fallback
    }
  }

  return result;
};

// Utility type for color paths (for TypeScript autocomplete)
export type ColorPath =
  | "primary.main"
  | "primary.dark"
  | "primary.light"
  | "secondary.main"
  | "secondary.dark"
  | "secondary.light"
  | "text.primary"
  | "text.secondary"
  | "text.disabled"
  | "background.default"
  | "background.paper"
  | "background.light"
  | "background.blueLight"
  | "background.messageBubble"
  | "background.primaryAlpha10"
  | "border.light"
  | "border.main"
  | "border.dark"
  | "success.500"
  | "warning.500"
  | "error.500"
  | "info.500";
